import { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart2, Users, Briefcase, Tag, Trash2, CheckCircle, XCircle, Clock, Star, ToggleLeft, ToggleRight, Shield, Ticket, Plus, Copy, Check, Calendar, Zap, Mail, QrCode, Download, ExternalLink } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import AdminEmailCampaigns from '../components/AdminEmailCampaigns';
import AdminSpotlightManager from '../components/AdminSpotlightManager';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const SITE_URL = 'https://useperkly.com';

// ─── Website QR Code Card ────────────────────────────────────────────────────
function WebsiteQRCard({ apiBase }) {
  const [qrUrl, setQrUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setQrUrl(`${apiBase}/admin/website-qr`);
    setLoading(false);
  }, [apiBase]);

  const handleDownload = async () => {
    try {
      const res = await fetch(`${apiBase}/admin/website-qr`, { credentials: 'include' });
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = 'perkly-qr-code.png';
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { /* silent */ }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(SITE_URL).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 p-6 mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-6" data-testid="website-qr-card">
      {/* QR image */}
      <div className="flex-shrink-0 p-3 bg-[#F6F1E9] rounded-2xl border border-[#8A9A8B]/10">
        {loading ? (
          <div className="w-[120px] h-[120px] flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <img
            src={`${apiBase}/admin/website-qr`}
            alt="Perkly Website QR Code"
            data-testid="website-qr-img"
            className="w-[120px] h-[120px] object-contain"
            onLoad={() => setLoading(false)}
          />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <QrCode size={16} className="text-[#C8A97E]" />
          <p className="font-bold text-[#2B2B2B] text-sm">Website QR Code</p>
        </div>
        <p className="text-xs text-[#2B2B2B]/55 mb-3 leading-relaxed">
          Share this QR code on flyers, social posts, and in-store signage.<br/>
          Scans link directly to <span className="text-[#C8A97E] font-semibold">useperkly.com</span>
        </p>

        {/* URL chip */}
        <div className="flex items-center gap-2 bg-[#F6F1E9] rounded-xl px-3 py-2 mb-4 max-w-xs">
          <ExternalLink size={12} className="text-[#8A9A8B] flex-shrink-0" />
          <span className="text-xs text-[#2B2B2B]/70 truncate font-mono">useperkly.com</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleDownload}
            data-testid="qr-download-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold text-white transition-opacity hover:opacity-90"
            style={{ background: '#2B2B2B' }}
          >
            <Download size={12} />
            Download PNG
          </button>
          <button
            onClick={handleCopy}
            data-testid="qr-copy-url-btn"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border transition-all"
            style={{
              borderColor: copied ? '#7BA05B' : '#C8A97E',
              color: copied ? '#7BA05B' : '#C8A97E',
              background: 'transparent',
            }}
          >
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied!' : 'Copy URL'}
          </button>
        </div>
      </div>
    </div>
  );
}

const STATUS_COLORS = {
  approved: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  pending: { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200' },
  rejected: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
};

function NotificationAction({ id, icon, title, desc, endpoint, btnLabel, btnColor, body, API, showMsg }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState('');
  const handleSend = async () => {
    if (!window.confirm(`Send: "${title}"?`)) return;
    setLoading(true); setResult('');
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json();
      // Build detailed result string
      let msg = data.message || (res.ok ? 'Done!' : 'Failed');
      const details = [];
      if (data.total_found !== undefined) details.push(`Found: ${data.total_found}`);
      if (data.total_skipped !== undefined) details.push(`Skipped: ${data.total_skipped}`);
      if (data.sent !== undefined && data.total_found !== undefined) details.push(`Sent: ${data.sent}`);
      if (data.errors > 0) details.push(`Errors: ${data.errors}`);
      if (details.length) msg += ` | ${details.join(' · ')}`;
      if (data.skipped_breakdown) {
        const bk = Object.entries(data.skipped_breakdown).map(([k,v]) => `${k}: ${v}`).join(', ');
        if (bk) msg += ` (${bk})`;
      }
      setResult(msg); showMsg(data.message || msg);
    } catch (e) { setResult('Error: ' + e.message); }
    setLoading(false);
  };
  return (
    <div className="bg-[#F6F1E9] rounded-2xl p-4 border border-[#8A9A8B]/15" data-testid={`notif-action-${id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <p className="font-bold text-sm text-[#2B2B2B] flex items-center gap-2">
            <span className="text-lg">{icon}</span> {title}
          </p>
          <p className="text-xs text-[#2B2B2B]/55 mt-1 leading-relaxed">{desc}</p>
          {result && <p className="text-xs font-semibold mt-2" style={{ color: result.toLowerCase().includes('error') ? '#ef4444' : '#5DBB63' }}>{result}</p>}
        </div>
        <button onClick={handleSend} disabled={loading}
          className="flex-shrink-0 px-4 py-2.5 rounded-full text-white text-xs font-bold transition-opacity disabled:opacity-60 hover:opacity-90"
          style={{ backgroundColor: btnColor }}>
          {loading ? 'Sending...' : btnLabel}
        </button>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState('');

  // Plan codes state
  const [planCodes, setPlanCodes] = useState([]);
  const [showCodeForm, setShowCodeForm] = useState(false);
  const [codeForm, setCodeForm] = useState({
    code: '', business_id: '', discount_percent: 20, plan_type: 'monthly', max_uses: 1, notes: '', expires_at: '',
  });
  const [codeFormLoading, setCodeFormLoading] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  // Subscription management state
  const [showSubModal, setShowSubModal] = useState(false);
  const [selectedBiz, setSelectedBiz] = useState(null);
  const [subForm, setSubForm] = useState({ plan_type: 'monthly', start_date: '', end_date: '', max_deals: 1, notes: '' });
  const [subLoading, setSubLoading] = useState(false);
  const [bizSubscriptions, setBizSubscriptions] = useState({});

  // Payments state
  const [transactions, setTransactions] = useState([]);

  const fetchStats = useCallback(async () => {
    const res = await fetch(`${API}/admin/stats`, { credentials: 'include' });
    if (res.ok) setStats(await res.json());
  }, []);

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/deals?limit=50`, { credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setDeals(data.deals || []);
    }
    setLoading(false);
  }, []);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/businesses`, { credentials: 'include' });
    if (res.ok) setBusinesses(await res.json());
    setLoading(false);
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/users`, { credentials: 'include' });
    if (res.ok) setUsers(await res.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  useEffect(() => {
    if (tab === 'deals') fetchDeals();
    else if (tab === 'businesses') { fetchBusinesses(); fetchAllSubscriptions(); }
    else if (tab === 'users') fetchUsers();
    else if (tab === 'plan-codes') fetchPlanCodes();
    else if (tab === 'payments') fetchTransactions();
    else if (tab === 'analytics') fetchAnalytics();
    else if (tab === 'settings') fetchHeroSettings();
    else if (tab === 'referrals') fetchAdminReferrals();
  }, [tab, fetchDeals, fetchBusinesses, fetchUsers]);

  const [adminReferrals, setAdminReferrals] = useState(null);
  const [recalcLoading, setRecalcLoading] = useState(false);
  const [recalcResult, setRecalcResult] = useState(null);

  const fetchAdminReferrals = async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/referrals`, { credentials: 'include' });
    if (res.ok) setAdminReferrals(await res.json());
    setLoading(false);
  };

  const handleRecalculateReferrals = async () => {
    setRecalcLoading(true);
    setRecalcResult(null);
    try {
      const res = await fetch(`${API}/admin/referrals/recalculate`, { method: 'POST', credentials: 'include' });
      const d = await res.json();
      setRecalcResult(d);
      fetchAdminReferrals(); // refresh table
    } catch (e) {
      setRecalcResult({ success: false, message: 'Request failed' });
    }
    setRecalcLoading(false);
  };

  const [heroSettings, setHeroSettings] = useState({ image_url: '', overlay_opacity: 55 });
  const [heroPreview, setHeroPreview] = useState('');
  const [heroLoading, setHeroLoading] = useState(false);
  const [heroMsg, setHeroMsg] = useState('');
  const heroFileRef = useRef(null);

  const fetchHeroSettings = async () => {
    const res = await fetch(`${API}/settings/hero`);
    if (res.ok) {
      const d = await res.json();
      setHeroSettings(d);
      setHeroPreview(d.image_url);
    }
  };

  const handleHeroImageFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setHeroMsg('File too large. Max 5MB.'); return; }
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API}/upload/image`, { method: 'POST', credentials: 'include', body: fd });
    if (res.ok) {
      const d = await res.json();
      setHeroSettings(s => ({ ...s, image_url: d.url }));
      setHeroPreview(d.url);
    }
  };

  const saveHeroSettings = async () => {
    setHeroLoading(true);
    setHeroMsg('');
    const res = await fetch(`${API}/admin/settings/hero`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ image_url: heroSettings.image_url, overlay_opacity: heroSettings.overlay_opacity }),
    });
    const d = await res.json();
    setHeroMsg(res.ok ? '✓ Hero image updated successfully!' : `Error: ${d.detail}`);
    setHeroLoading(false);
  };

  const [analytics, setAnalytics] = useState(null);
  const fetchAnalytics = async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/analytics`, { credentials: 'include' });
    if (res.ok) setAnalytics(await res.json());
    setLoading(false);
  };

  const handleDeleteBusiness = async (bizId, bizName) => {
    if (!window.confirm(`Permanently delete "${bizName}" and all its deals, plans, and data? This cannot be undone.`)) return;
    const res = await fetch(`${API}/admin/businesses/${bizId}`, { method: 'DELETE', credentials: 'include' });
    if (res.ok) {
      setBusinesses(prev => prev.filter(b => b.business_id !== bizId));
      setBizSubscriptions(prev => { const n = { ...prev }; delete n[bizId]; return n; });
      showMsg(`"${bizName}" permanently deleted`);
    }
  };

  const handleDeleteAllBusinesses = async () => {
    if (!window.confirm('DELETE ALL businesses and all their data permanently? This cannot be undone.')) return;
    if (!window.confirm('Are you absolutely sure? All businesses, deals, plans, and PIN schedules will be erased.')) return;
    const res = await fetch(`${API}/admin/businesses`, { method: 'DELETE', credentials: 'include' });
    const data = await res.json();
    if (res.ok) { setBusinesses([]); setBizSubscriptions({}); showMsg(data.message); }
  };

  const handleGeocodeBusiness = async (bizId) => {
    try {
      const res = await fetch(`${API}/admin/businesses/${bizId}/geocode`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (res.ok) showMsg(`Geocoded! ${data.message}`);
      else showMsg(`Error: ${data.detail}`);
    } catch { showMsg('Geocoding failed'); }
  };

  const fetchTransactions = async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/transactions`, { credentials: 'include' });
    if (res.ok) setTransactions(await res.json());
    setLoading(false);
  };

  const fetchAllSubscriptions = async () => {
    const res = await fetch(`${API}/admin/subscriptions`, { credentials: 'include' });
    if (res.ok) {
      const subs = await res.json();
      const map = {};
      subs.filter(s => s.is_active).forEach(s => { map[s.business_id] = s; });
      setBizSubscriptions(map);
    }
  };

  const openSubModal = (biz) => {
    const today = new Date().toISOString().slice(0, 10);
    const existing = bizSubscriptions[biz.business_id];
    setSelectedBiz(biz);
    setSubForm({
      plan_type: existing?.plan_type || 'monthly',
      start_date: existing?.start_date || today,
      end_date: existing?.end_date || '',
      max_deals: existing?.max_deals || 1,
      notes: existing?.notes || '',
    });
    setShowSubModal(true);
  };

  const calcEndDate = (startDate, planType) => {
    if (!startDate) return '';
    const d = new Date(startDate);
    if (planType === 'weekly') d.setDate(d.getDate() + 7);
    else if (planType === 'monthly') d.setDate(d.getDate() + 30);
    else return subForm.end_date;
    return d.toISOString().slice(0, 10);
  };

  const handleSubPlanChange = (planType) => {
    const end = calcEndDate(subForm.start_date, planType);
    setSubForm(f => ({ ...f, plan_type: planType, end_date: planType !== 'custom' ? end : f.end_date }));
  };

  const handleSubStartChange = (startDate) => {
    const end = subForm.plan_type !== 'custom' ? calcEndDate(startDate, subForm.plan_type) : subForm.end_date;
    setSubForm(f => ({ ...f, start_date: startDate, end_date: end }));
  };

  const handleActivateSubscription = async (e) => {
    e.preventDefault();
    setSubLoading(true);
    try {
      const res = await fetch(`${API}/admin/businesses/${selectedBiz.business_id}/subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...subForm, max_deals: Number(subForm.max_deals) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setBizSubscriptions(prev => ({ ...prev, [selectedBiz.business_id]: data }));
      setShowSubModal(false);
      showMsg(`Subscription activated for ${selectedBiz.name} until ${subForm.end_date}`);
    } catch (err) {
      showMsg(`Error: ${err.message}`);
    }
    setSubLoading(false);
  };

  const handleCancelSubscription = async (bizId) => {
    if (!window.confirm('Cancel this subscription? All deals will be deactivated.')) return;
    await fetch(`${API}/admin/businesses/${bizId}/subscription`, { method: 'DELETE', credentials: 'include' });
    setBizSubscriptions(prev => { const n = { ...prev }; delete n[bizId]; return n; });
    showMsg('Subscription cancelled');
  };

  const fetchPlanCodes = async () => {
    setLoading(true);
    const res = await fetch(`${API}/admin/plan-codes`, { credentials: 'include' });
    if (res.ok) setPlanCodes(await res.json());
    setLoading(false);
  };

  const handleCreatePlanCode = async (e) => {
    e.preventDefault();
    setCodeFormLoading(true);
    try {
      const res = await fetch(`${API}/admin/plan-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...codeForm, discount_percent: Number(codeForm.discount_percent), max_uses: Number(codeForm.max_uses) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed');
      setPlanCodes((prev) => [data, ...prev]);
      setShowCodeForm(false);
      setCodeForm({ code: '', business_id: '', discount_percent: 20, plan_type: 'monthly', max_uses: 1, notes: '', expires_at: '' });
      showMsg(`Plan code "${data.code}" created!`);
    } catch (err) {
      showMsg(`Error: ${err.message}`);
    }
    setCodeFormLoading(false);
  };

  const handleTogglePlanCode = async (codeId) => {
    const res = await fetch(`${API}/admin/plan-codes/${codeId}/toggle`, { method: 'PUT', credentials: 'include' });
    if (res.ok) {
      const data = await res.json();
      setPlanCodes((prev) => prev.map((c) => c.code_id === codeId ? { ...c, is_active: data.is_active } : c));
    }
  };

  const handleDeletePlanCode = async (codeId) => {
    if (!window.confirm('Delete this plan code?')) return;
    await fetch(`${API}/admin/plan-codes/${codeId}`, { method: 'DELETE', credentials: 'include' });
    setPlanCodes((prev) => prev.filter((c) => c.code_id !== codeId));
    showMsg('Code deleted');
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const autoGenerateCode = () => {
    const biz = businesses.find((b) => b.business_id === codeForm.business_id);
    const prefix = biz ? biz.name.replace(/[^A-Za-z]/g, '').toUpperCase().slice(0, 4) : 'PERK';
    const num = Math.floor(Math.random() * 40) + 10;
    setCodeForm((f) => ({ ...f, code: `PERK${prefix}${num}` }));
  };

  const showMsg = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(''), 3000);
  };

  const handleBusinessStatus = async (businessId, status) => {
    const res = await fetch(`${API}/admin/businesses/${businessId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status }),
    });
    if (res.ok) {
      setBusinesses((prev) => prev.map((b) => b.business_id === businessId ? { ...b, status } : b));
      showMsg(`Business ${status} successfully`);
    }
  };

  const handleToggleFeatured = async (deal) => {
    const res = await fetch(`${API}/admin/deals/${deal.deal_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_featured: !deal.is_featured }),
    });
    if (res.ok) {
      setDeals((prev) => prev.map((d) => d.deal_id === deal.deal_id ? { ...d, is_featured: !d.is_featured } : d));
    }
  };

  const handleToggleActive = async (deal) => {
    const res = await fetch(`${API}/admin/deals/${deal.deal_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ is_active: !deal.is_active }),
    });
    if (res.ok) {
      setDeals((prev) => prev.map((d) => d.deal_id === deal.deal_id ? { ...d, is_active: !d.is_active } : d));
    }
  };

  const handleDeleteDeal = async (dealId) => {
    if (!window.confirm('Delete this deal?')) return;
    await fetch(`${API}/admin/deals/${dealId}`, { method: 'DELETE', credentials: 'include' });
    setDeals((prev) => prev.filter((d) => d.deal_id !== dealId));
    showMsg('Deal deleted');
  };

  const handleUpdateUserRole = async (userId, role) => {
    const res = await fetch(`${API}/admin/users/${userId}/role`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ role }),
    });
    if (res.ok) {
      setUsers((prev) => prev.map((u) => u.user_id === userId ? { ...u, role } : u));
      showMsg('User role updated');
    }
  };

  const TABS = [
    { id: 'overview',   label: 'Overview',   icon: BarChart2 },
    { id: 'analytics',  label: 'Analytics',  icon: Star },
    { id: 'deals',      label: 'Deals',      icon: Tag },
    { id: 'businesses', label: 'Businesses', icon: Briefcase },
    { id: 'users',      label: 'Users',      icon: Users },
    { id: 'referrals',  label: 'Referrals',  icon: Zap },
    { id: 'email',      label: 'Email Campaigns', icon: Mail },
    { id: 'spotlight',  label: 'Spotlight',       icon: Star },
    { id: 'plan-codes', label: 'Plan Codes', icon: Ticket },
    { id: 'payments',   label: 'Payments',   icon: Zap },
    { id: 'settings',   label: 'Settings',   icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-[#F6F1E9]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-[#C8A97E]/15 flex items-center justify-center">
            <Shield size={20} className="text-[#C8A97E]" />
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A8B]">Admin Panel</p>
            <h1 className="text-2xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="admin-title">
              Dashboard
            </h1>
          </div>
        </div>

        {/* Action Message */}
        {actionMsg && (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl p-3 mb-5">
            <CheckCircle size={16} /> {actionMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-2xl p-1.5 border border-[#8A9A8B]/10 mb-8 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap ${
                tab === t.id
                  ? 'bg-[#2B2B2B] text-[#F6F1E9] shadow-sm'
                  : 'text-[#2B2B2B]/60 hover:text-[#2B2B2B]'
              }`}
            >
              <t.icon size={15} /> {t.label}
              {t.id === 'businesses' && stats?.pending_businesses > 0 && (
                <span className="w-5 h-5 rounded-full bg-[#C8A97E] text-white text-xs flex items-center justify-center">
                  {stats.pending_businesses}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* OVERVIEW */}
        {tab === 'overview' && (
          <div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8" data-testid="admin-stats-grid">
              {[
                { label: 'Total Deals', value: stats?.total_deals ?? '—', color: '#C8A97E', icon: Tag },
                { label: 'Active Deals', value: stats?.active_deals ?? '—', color: '#7BA05B', icon: CheckCircle },
                { label: 'Total Businesses', value: stats?.total_businesses ?? '—', color: '#8A9A8B', icon: Briefcase },
                { label: 'Pending Approval', value: stats?.pending_businesses ?? '—', color: '#C8957E', icon: Clock },
                { label: 'Total Users', value: stats?.total_users ?? '—', color: '#A07BC8', icon: Users },
                { label: 'Email Signups', value: stats?.email_signups ?? '—', color: '#C87B7B', icon: Star },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-2xl p-5 border border-[#8A9A8B]/10 flex items-center gap-4" data-testid={`admin-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: s.color + '15' }}>
                    <s.icon size={18} style={{ color: s.color }} />
                  </div>
                  <div>
                    <p className="text-xs text-[#2B2B2B]/50 mb-0.5">{s.label}</p>
                    <p className="text-2xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>{s.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {stats?.pending_businesses > 0 && (
              <div className="bg-[#C8A97E]/10 border border-[#C8A97E]/30 rounded-2xl p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-[#C8A97E]" />
                  <div>
                    <p className="font-bold text-[#2B2B2B] text-sm">{stats.pending_businesses} business{stats.pending_businesses > 1 ? 'es' : ''} waiting for approval</p>
                    <p className="text-xs text-[#2B2B2B]/55">Review them in the Businesses tab</p>
                  </div>
                </div>
                <button onClick={() => setTab('businesses')} className="btn-primary text-xs px-4 py-2">Review Now</button>
              </div>
            )}

            {/* Website QR Code Card */}
            <WebsiteQRCard apiBase={API} />

            {/* Fix map coords utility */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 flex items-center justify-between mt-4">
              <div className="flex items-center gap-3">
                <span className="text-xl">📍</span>
                <div>
                  <p className="font-bold text-blue-700 text-sm">Fix Map Coordinates</p>
                  <p className="text-xs text-blue-600/80">If promoted deals aren&apos;t showing on the map, run this to auto-fix missing coordinates.</p>
                </div>
              </div>
              <button
                data-testid="fix-map-coords-btn"
                onClick={async () => {
                  const res = await fetch(`${API}/admin/deals/fix-map-coords`, { method: 'POST', credentials: 'include' });
                  const d = await res.json();
                  showMsg(d.message || 'Done');
                }}
                className="px-4 py-2 rounded-full bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 transition-colors flex-shrink-0"
              >
                Fix Now
              </button>
            </div>
          </div>
        )}

        {/* DEALS */}
        {tab === 'deals' && (
          <div data-testid="admin-deals-panel">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#8A9A8B]/10 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F6F1E9] border-b border-[#8A9A8B]/15">
                        {['Deal', 'Business', 'Category', 'City', 'Featured', 'Active', 'Views', ''].map((h) => (
                          <th key={h} className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#8A9A8B]/10">
                      {deals.map((deal) => (
                        <tr key={deal.deal_id} className="hover:bg-[#F6F1E9]/40 transition-colors" data-testid={`admin-deal-row-${deal.deal_id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <img src={deal.image_url} alt="" className="w-9 h-9 rounded-lg object-cover flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                              <span className="text-sm font-semibold text-[#2B2B2B] line-clamp-1 max-w-[160px]">{deal.title}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#2B2B2B]/60">{deal.business_name}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-[#8A9A8B]/10 text-[#8A9A8B] px-2 py-0.5 rounded-full">{deal.category}</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#2B2B2B]/60">{deal.city_name}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleToggleFeatured(deal)} data-testid={`admin-toggle-featured-${deal.deal_id}`}>
                              {deal.is_featured
                                ? <Star size={18} className="text-[#C8A97E]" fill="#C8A97E" />
                                : <Star size={18} className="text-[#8A9A8B]/40" />
                              }
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleToggleActive(deal)} data-testid={`admin-toggle-active-${deal.deal_id}`}>
                              {deal.is_active
                                ? <ToggleRight size={20} className="text-green-500" />
                                : <ToggleLeft size={20} className="text-[#8A9A8B]" />
                              }
                            </button>
                          </td>
                          <td className="px-4 py-3 text-sm text-[#2B2B2B]/60">{deal.views}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleDeleteDeal(deal.deal_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition-colors" data-testid={`admin-delete-deal-${deal.deal_id}`}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* BUSINESSES */}
        {tab === 'businesses' && (
          <div data-testid="admin-businesses-panel">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Businesses</h2>
                <p className="text-xs text-[#2B2B2B]/55 mt-0.5">{businesses.length} registered</p>
              </div>
              {businesses.length > 0 && (
                <button
                  onClick={handleDeleteAllBusinesses}
                  data-testid="delete-all-businesses-btn"
                  className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500 text-white text-xs font-bold hover:bg-red-600 transition-colors"
                >
                  <Trash2 size={13} /> Delete All
                </button>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {businesses.map((biz) => {
                  const colors = STATUS_COLORS[biz.status] || STATUS_COLORS.pending;
                  const sub = bizSubscriptions[biz.business_id];
                  const daysLeft = sub ? Math.ceil((new Date(sub.end_date + 'T23:59:59') - new Date()) / (1000*60*60*24)) : null;
                  return (
                    <div key={biz.business_id} className="bg-white rounded-2xl p-5 border border-[#8A9A8B]/10" data-testid={`admin-business-row-${biz.business_id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-bold text-[#2B2B2B]">{biz.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${colors.bg} ${colors.text} ${colors.border}`}>
                              {biz.status.charAt(0).toUpperCase() + biz.status.slice(1)}
                            </span>
                          </div>
                          <p className="text-xs text-[#2B2B2B]/55">{biz.category} · {biz.city_name} · {biz.email}</p>
                          <p className="text-xs text-[#2B2B2B]/40 mt-0.5">
                            {biz.address}
                            {biz.tax_id && <span className="ml-2 text-[#8A9A8B]">EIN: {biz.tax_id}</span>}
                            {biz.auto_approved && <span className="ml-2 text-green-600 font-semibold">✓ Auto-verified</span>}
                          </p>

                          {/* Trial / Subscription Status — prominent visual block */}
                          {(() => {
                            const today = new Date();
                            const trialEnd   = biz.trial_end_date ? new Date(biz.trial_end_date + 'T23:59:59') : null;
                            const trialDaysLeft = trialEnd ? Math.ceil((trialEnd - today) / (1000*60*60*24)) : -1;
                            const trialPct   = trialEnd ? Math.max(0, Math.min(100, (trialDaysLeft / 60) * 100)) : 0;
                            const isTrialUrgent = trialDaysLeft > 0 && trialDaysLeft <= 7;

                            if (sub) {
                              const subDaysLeft = Math.ceil((new Date(sub.end_date + 'T23:59:59') - today) / (1000*60*60*24));
                              const planDuration = sub.plan_type === 'weekly' ? 7 : sub.plan_type === 'campaign' ? 14 : 30;
                              const subPct = Math.max(0, Math.min(100, (subDaysLeft / planDuration) * 100));
                              const isUrgent = subDaysLeft <= 3;
                              const planLabel = sub.plan_type === 'weekly' ? 'Weekly Deal Boost' : sub.plan_type === 'campaign' ? 'Campaign Boost' : 'Monthly Growth';
                              const planColor = sub.plan_type === 'monthly' ? '#A07BC8' : sub.plan_type === 'campaign' ? '#C8A97E' : '#8A9A8B';
                              return (
                                <div className={`mt-2 rounded-xl p-3 border ${isUrgent ? 'bg-orange-50 border-orange-200' : 'bg-[#F6F1E9] border-[#C8A97E]/25'}`}>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-bold text-[#2B2B2B] px-2 py-0.5 rounded-full text-white text-xs" style={{ background: planColor }}>
                                        💳 {planLabel}
                                      </span>
                                      {isUrgent && <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">⚠️ Expiring soon!</span>}
                                    </div>
                                    <span className={`text-xs font-bold ${isUrgent ? 'text-orange-600' : 'text-[#2B2B2B]/60'}`}>
                                      {subDaysLeft <= 0 ? '❌ EXPIRED' : `${subDaysLeft} day${subDaysLeft !== 1 ? 's' : ''} left`}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-[#2B2B2B]/50 mb-2">
                                    <span>📅 {sub.start_date}</span>
                                    <span>→</span>
                                    <span className="font-semibold text-[#2B2B2B]/70">{sub.end_date}</span>
                                    <span>·</span>
                                    <span>Max {sub.max_deals} deal{sub.max_deals > 1 ? 's' : ''}</span>
                                  </div>
                                  <div className="h-1.5 rounded-full bg-[#8A9A8B]/15 overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${subPct}%`, background: isUrgent ? '#f97316' : planColor }} />
                                  </div>
                                </div>
                              );
                            }

                            if (trialDaysLeft > 0) {
                              return (
                                <div className={`mt-2 rounded-xl p-3 border ${isTrialUrgent ? 'bg-yellow-50 border-yellow-200' : 'bg-emerald-50 border-emerald-200'}`}>
                                  <div className="flex items-center justify-between mb-1.5">
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isTrialUrgent ? 'bg-yellow-100 text-yellow-700 border border-yellow-300' : 'bg-emerald-100 text-emerald-700 border border-emerald-300'}`}>
                                      🆓 Free Trial Active
                                    </span>
                                    <span className={`text-xs font-bold ${isTrialUrgent ? 'text-yellow-700' : 'text-emerald-700'}`}>
                                      {trialDaysLeft} day{trialDaysLeft !== 1 ? 's' : ''} remaining
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs text-[#2B2B2B]/50 mb-2">
                                    <span>Ends</span>
                                    <span className="font-semibold text-[#2B2B2B]/70">{biz.trial_end_date}</span>
                                    <span>·</span>
                                    <span>1 active deal · No paid plan yet</span>
                                  </div>
                                  <div className="h-1.5 rounded-full bg-[#8A9A8B]/15 overflow-hidden">
                                    <div className="h-full rounded-full transition-all" style={{ width: `${trialPct}%`, background: isTrialUrgent ? '#eab308' : '#10b981' }} />
                                  </div>
                                </div>
                              );
                            }

                            if (trialEnd && trialDaysLeft <= 0) {
                              return (
                                <div className="mt-2 rounded-xl p-3 border bg-red-50 border-red-200">
                                  <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full border border-red-300">
                                      ❌ Free Trial Expired
                                    </span>
                                    <span className="text-xs text-red-500">Ended {biz.trial_end_date}</span>
                                  </div>
                                  <p className="text-xs text-red-500/75 mt-1">Business needs a paid plan to list deals. Activate a plan or their deals stay paused.</p>
                                </div>
                              );
                            }

                            return (
                              <div className="mt-2 rounded-xl p-2.5 border bg-[#F6F1E9] border-[#8A9A8B]/20">
                                <p className="text-xs text-[#8A9A8B]">⏳ No subscription or trial data found</p>
                              </div>
                            );
                          })()}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 flex-shrink-0">
                          {/* Subscription button */}
                          <button
                            data-testid={`manage-subscription-${biz.business_id}`}
                            onClick={() => openSubModal(biz)}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
                              sub ? 'bg-[#C8A97E]/10 text-[#C8A97E] border-[#C8A97E]/30 hover:bg-[#C8A97E]/20'
                                  : 'bg-[#F6F1E9] text-[#2B2B2B] border-[#8A9A8B]/30 hover:border-[#C8A97E]'
                            }`}
                          >
                            <Calendar size={12} /> {sub ? 'Edit Plan' : 'Activate Plan'}
                          </button>
                          {/* Fix map coordinates if missing */}
                          {!biz.lat && (
                            <button
                              data-testid={`geocode-business-${biz.business_id}`}
                              onClick={() => handleGeocodeBusiness(biz.business_id)}
                              title="Auto-detect map coordinates from address"
                              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-blue-50 text-blue-600 text-xs font-bold border border-blue-200 hover:bg-blue-100 transition-colors"
                            >
                              📍 Fix Map
                            </button>
                          )}
                          {sub && (
                            <button
                              onClick={() => handleCancelSubscription(biz.business_id)}
                              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-red-50 text-red-500 text-xs font-bold border border-red-200 hover:bg-red-100 transition-colors"
                            >
                              Cancel Plan
                            </button>
                          )}
                          {biz.status !== 'approved' && (
                            <button data-testid={`approve-business-${biz.business_id}`} onClick={() => handleBusinessStatus(biz.business_id, 'approved')} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-green-50 text-green-600 text-xs font-bold border border-green-200 hover:bg-green-100 transition-colors">
                              <CheckCircle size={13} /> Approve
                            </button>
                          )}
                          {biz.status !== 'rejected' && (
                            <button data-testid={`reject-business-${biz.business_id}`} onClick={() => handleBusinessStatus(biz.business_id, 'rejected')} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-50 text-red-500 text-xs font-bold border border-red-200 hover:bg-red-100 transition-colors">
                              <XCircle size={13} /> Reject
                            </button>
                          )}
                          {biz.status !== 'pending' && (
                            <button onClick={() => handleBusinessStatus(biz.business_id, 'pending')} className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-yellow-50 text-yellow-600 text-xs font-bold border border-yellow-200 hover:bg-yellow-100 transition-colors">
                              <Clock size={13} /> Reset
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteBusiness(biz.business_id, biz.name)}
                            data-testid={`delete-business-${biz.business_id}`}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-600 text-white text-xs font-bold border border-red-600 hover:bg-red-700 transition-colors"
                            title="Permanently delete"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {businesses.length === 0 && (
                  <div className="text-center py-12 text-[#2B2B2B]/40">No businesses registered yet.</div>
                )}
              </div>
            )}

            {/* Activate Subscription Modal */}
            {showSubModal && selectedBiz && (
              <div className="fixed inset-0 bg-[#2B2B2B]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl w-full shadow-xl flex flex-col" style={{ maxWidth: '500px', maxHeight: '92vh' }} data-testid="subscription-modal">
                  <div className="flex items-center justify-between px-7 pt-6 pb-4 border-b border-[#8A9A8B]/12 flex-shrink-0">
                    <div>
                      <h3 className="text-lg font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Activate Plan</h3>
                      <p className="text-xs text-[#2B2B2B]/55 mt-0.5">{selectedBiz.name}</p>
                    </div>
                    <button onClick={() => setShowSubModal(false)} className="w-8 h-8 rounded-full bg-[#F6F1E9] text-[#2B2B2B]/60 flex items-center justify-center text-lg">×</button>
                  </div>

                  <div className="overflow-y-auto flex-1 px-7 py-5">
                    <form onSubmit={handleActivateSubscription} id="sub-form" className="space-y-4">
                      {/* Plan type */}
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-2">Plan Type *</label>
                        <div className="grid grid-cols-3 gap-2">
                          {[
                            { id: 'weekly', label: 'Weekly', sub: '7 days', deals: 1 },
                            { id: 'monthly', label: 'Monthly', sub: '30 days', deals: 3 },
                            { id: 'custom', label: 'Custom', sub: 'You set dates', deals: 1 },
                          ].map((p) => (
                            <button
                              key={p.id} type="button"
                              onClick={() => { handleSubPlanChange(p.id); setSubForm(f => ({ ...f, max_deals: p.deals })); }}
                              className={`py-3 px-2 rounded-xl border-2 text-center transition-all ${subForm.plan_type === p.id ? 'border-[#C8A97E] bg-[#C8A97E]/8' : 'border-[#8A9A8B]/20 hover:border-[#C8A97E]/40'}`}
                            >
                              <p className="text-sm font-bold text-[#2B2B2B]">{p.label}</p>
                              <p className="text-xs text-[#8A9A8B] mt-0.5">{p.sub}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Start Date *</label>
                          <input
                            type="date" required value={subForm.start_date}
                            onChange={(e) => handleSubStartChange(e.target.value)}
                            className="perkly-input" data-testid="sub-start-date"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">
                            End Date * {subForm.plan_type !== 'custom' && <span className="normal-case text-[#C8A97E] font-normal">(auto)</span>}
                          </label>
                          <input
                            type="date" required value={subForm.end_date}
                            onChange={(e) => setSubForm(f => ({ ...f, end_date: e.target.value }))}
                            readOnly={subForm.plan_type !== 'custom'}
                            className={`perkly-input ${subForm.plan_type !== 'custom' ? 'opacity-60 bg-[#F6F1E9] cursor-not-allowed' : ''}`}
                            data-testid="sub-end-date"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Max Active Deals</label>
                        <select value={subForm.max_deals} onChange={(e) => setSubForm(f => ({ ...f, max_deals: e.target.value }))} className="perkly-input">
                          {[1, 2, 3, 5].map(n => <option key={n} value={n}>{n} deal{n > 1 ? 's' : ''}</option>)}
                        </select>
                      </div>

                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Notes (internal)</label>
                        <textarea rows={2} value={subForm.notes} onChange={(e) => setSubForm(f => ({ ...f, notes: e.target.value }))} placeholder="e.g. Paid via Stripe on 4/16/2026" className="perkly-input resize-none" />
                      </div>

                      {subForm.start_date && subForm.end_date && (
                        <div className="bg-[#C8A97E]/8 border border-[#C8A97E]/25 rounded-xl p-4 text-sm">
                          <p className="font-semibold text-[#2B2B2B] mb-1">Summary</p>
                          <p className="text-xs text-[#2B2B2B]/65">
                            <strong>{selectedBiz.name}</strong> will have an active {subForm.plan_type} plan from <strong>{subForm.start_date}</strong> to <strong>{subForm.end_date}</strong> with up to <strong>{subForm.max_deals}</strong> deal{subForm.max_deals > 1 ? 's' : ''}.
                            All their deals will auto-deactivate on the end date.
                          </p>
                        </div>
                      )}
                    </form>
                  </div>

                  <div className="px-7 py-5 border-t border-[#8A9A8B]/12 flex gap-3 bg-white rounded-b-3xl flex-shrink-0">
                    <button type="button" onClick={() => setShowSubModal(false)} className="flex-1 btn-secondary text-sm py-3 justify-center">Cancel</button>
                    <button form="sub-form" type="submit" disabled={subLoading} className="flex-1 btn-primary text-sm py-3 justify-center disabled:opacity-60" data-testid="activate-subscription-submit">
                      {subLoading ? 'Activating...' : 'Activate Plan'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* USERS */}
        {tab === 'users' && (
          <div data-testid="admin-users-panel">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#8A9A8B]/10 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F6F1E9] border-b border-[#8A9A8B]/15">
                        {['User', 'Email', 'Role', 'Joined', 'Actions'].map((h) => (
                          <th key={h} className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-5 py-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#8A9A8B]/10">
                      {users.map((u) => (
                        <tr key={u.user_id} className="hover:bg-[#F6F1E9]/40 transition-colors" data-testid={`admin-user-row-${u.user_id}`}>
                          <td className="px-5 py-3">
                            <div className="flex items-center gap-2.5">
                              {u.picture ? (
                                <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[#C8A97E]/20 flex items-center justify-center text-xs font-bold text-[#C8A97E]">
                                  {u.name?.[0]}
                                </div>
                              )}
                              <span className="text-sm font-semibold text-[#2B2B2B]">{u.name}</span>
                            </div>
                          </td>
                          <td className="px-5 py-3 text-sm text-[#2B2B2B]/60">{u.email}</td>
                          <td className="px-5 py-3">
                            <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${
                              u.role === 'admin' ? 'bg-[#C8A97E]/15 text-[#C8A97E]'
                              : u.role === 'business' ? 'bg-blue-50 text-blue-600'
                              : 'bg-[#8A9A8B]/10 text-[#8A9A8B]'
                            }`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-xs text-[#2B2B2B]/50">{u.created_at?.slice(0, 10) || '—'}</td>
                          <td className="px-5 py-3">
                            {u.user_id !== user?.user_id && (
                              <select
                                value={u.role}
                                onChange={(e) => handleUpdateUserRole(u.user_id, e.target.value)}
                                className="text-xs border border-[#8A9A8B]/30 rounded-lg px-2 py-1 bg-white focus:outline-none focus:border-[#C8A97E]"
                                data-testid={`admin-user-role-select-${u.user_id}`}
                              >
                                <option value="user">user</option>
                                <option value="business">business</option>
                                <option value="admin">admin</option>
                              </select>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* EMAIL CAMPAIGNS */}
        {tab === 'email' && (
          <div data-testid="admin-email-panel">
            <AdminEmailCampaigns showMsg={showMsg} />
          </div>
        )}

        {/* SPOTLIGHT MANAGER */}
        {tab === 'spotlight' && (
          <div data-testid="admin-spotlight-panel">
            <AdminSpotlightManager showMsg={showMsg} />
          </div>
        )}

        {/* PLAN CODES */}
        {tab === 'plan-codes' && (
          <div data-testid="admin-plan-codes-panel">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Plan Promo Codes</h2>
                <p className="text-xs text-[#2B2B2B]/55 mt-0.5">Generate discount codes for businesses purchasing plans on Perkly.</p>
              </div>
              <button
                data-testid="generate-plan-code-button"
                onClick={() => setShowCodeForm(true)}
                className="btn-primary text-sm px-5 py-2.5 inline-flex"
              >
                <Plus size={15} /> Generate Code
              </button>
            </div>

            {/* Summary row */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[
                { label: 'Total Codes', value: planCodes.length, color: '#C8A97E' },
                { label: 'Active', value: planCodes.filter((c) => c.is_active).length, color: '#7BA05B' },
                { label: 'Total Uses', value: planCodes.reduce((s, c) => s + (c.uses_count || 0), 0), color: '#8A9A8B' },
              ].map((s) => (
                <div key={s.label} className="bg-white rounded-2xl p-4 border border-[#8A9A8B]/10 text-center">
                  <p className="text-xs text-[#2B2B2B]/50 mb-1">{s.label}</p>
                  <p className="text-2xl font-bold" style={{ color: s.color, fontFamily: 'Playfair Display, serif' }}>{s.value}</p>
                </div>
              ))}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : planCodes.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-[#8A9A8B]/10">
                <div className="w-14 h-14 rounded-full bg-[#C8A97E]/10 flex items-center justify-center mx-auto mb-4">
                  <Ticket size={26} className="text-[#C8A97E]" />
                </div>
                <h3 className="font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No Plan Codes Yet</h3>
                <p className="text-sm text-[#2B2B2B]/55 mb-5">Generate a discount code for a business when they purchase a plan.</p>
                <button onClick={() => setShowCodeForm(true)} className="btn-primary inline-flex text-sm">
                  <Plus size={14} /> Generate First Code
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#8A9A8B]/10 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F6F1E9] border-b border-[#8A9A8B]/15">
                        {['Code', 'Business', 'Discount', 'Plan', 'Uses', 'Expires', 'Status', ''].map((h) => (
                          <th key={h} className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#8A9A8B]/10">
                      {planCodes.map((pc) => (
                        <tr key={pc.code_id} className="hover:bg-[#F6F1E9]/40" data-testid={`plan-code-row-${pc.code_id}`}>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-bold text-sm text-[#2B2B2B] tracking-wider bg-[#F6F1E9] px-2.5 py-1 rounded-lg">{pc.code}</span>
                              <button
                                onClick={() => handleCopyCode(pc.code)}
                                className="text-[#8A9A8B] hover:text-[#C8A97E] transition-colors"
                                data-testid={`copy-plan-code-${pc.code_id}`}
                              >
                                {copiedCode === pc.code ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#2B2B2B]/60">{pc.business_name || <span className="italic text-[#8A9A8B]">General</span>}</td>
                          <td className="px-4 py-3">
                            <span className="text-sm font-bold text-[#C8A97E]">{pc.discount_percent}% off</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-[#8A9A8B]/10 text-[#8A9A8B] px-2 py-1 rounded-full capitalize">{pc.plan_type}</span>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#2B2B2B]/60">{pc.uses_count} / {pc.max_uses === 9999 ? '∞' : pc.max_uses}</td>
                          <td className="px-4 py-3 text-xs text-[#2B2B2B]/50">{pc.expires_at || '—'}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleTogglePlanCode(pc.code_id)} data-testid={`toggle-plan-code-${pc.code_id}`}>
                              {pc.is_active
                                ? <ToggleRight size={20} className="text-green-500" />
                                : <ToggleLeft size={20} className="text-[#8A9A8B]" />}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleDeletePlanCode(pc.code_id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-400 transition-colors" data-testid={`delete-plan-code-${pc.code_id}`}>
                              <Trash2 size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Create Plan Code Modal */}
            {showCodeForm && (
              <div className="fixed inset-0 bg-[#2B2B2B]/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-3xl w-full shadow-xl flex flex-col" style={{ maxWidth: '520px', maxHeight: '92vh' }} data-testid="plan-code-form-modal">
                  <div className="flex items-center justify-between px-7 pt-6 pb-4 border-b border-[#8A9A8B]/12 flex-shrink-0">
                    <div>
                      <h3 className="text-lg font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Generate Plan Code</h3>
                      <p className="text-xs text-[#2B2B2B]/50 mt-0.5">Create a discount code for a business purchasing a plan</p>
                    </div>
                    <button onClick={() => setShowCodeForm(false)} className="w-8 h-8 rounded-full bg-[#F6F1E9] text-[#2B2B2B]/60 flex items-center justify-center text-lg">×</button>
                  </div>

                  <div className="overflow-y-auto flex-1 px-7 py-5">
                    <form onSubmit={handleCreatePlanCode} id="plan-code-form" className="space-y-4">
                      {/* Code field with auto-generate */}
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Promo Code *</label>
                        <div className="flex gap-2">
                          <input
                            required
                            value={codeForm.code}
                            onChange={(e) => setCodeForm({ ...codeForm, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                            placeholder="e.g. PERK50OFF"
                            className="perkly-input flex-1 font-mono tracking-widest"
                            data-testid="plan-code-input"
                          />
                          <button
                            type="button"
                            onClick={autoGenerateCode}
                            className="px-3 py-2.5 rounded-xl bg-[#F6F1E9] text-xs font-bold text-[#C8A97E] border border-[#C8A97E]/30 hover:bg-[#C8A97E]/10 transition-colors whitespace-nowrap"
                            data-testid="auto-generate-code-button"
                          >
                            Auto-generate
                          </button>
                        </div>
                      </div>

                      {/* Business selector */}
                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Assign to Business (optional)</label>
                        <select
                          value={codeForm.business_id}
                          onChange={(e) => setCodeForm({ ...codeForm, business_id: e.target.value })}
                          className="perkly-input"
                          data-testid="plan-code-business-select"
                        >
                          <option value="">— General / Any Business —</option>
                          {businesses.map((b) => (
                            <option key={b.business_id} value={b.business_id}>{b.name} ({b.city_name})</option>
                          ))}
                        </select>
                        <p className="text-xs text-[#2B2B2B]/45 mt-1">Leave blank to create a general code usable by any business.</p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Discount %</label>
                          <select
                            value={codeForm.discount_percent}
                            onChange={(e) => setCodeForm({ ...codeForm, discount_percent: Number(e.target.value) })}
                            className="perkly-input"
                            data-testid="plan-code-discount-select"
                          >
                            {[10, 15, 20, 25, 30, 50, 100].map((d) => (
                              <option key={d} value={d}>{d}% off</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Plan Type</label>
                          <select
                            value={codeForm.plan_type}
                            onChange={(e) => setCodeForm({ ...codeForm, plan_type: e.target.value })}
                            className="perkly-input"
                            data-testid="plan-code-type-select"
                          >
                            <option value="weekly">Weekly Boost</option>
                            <option value="monthly">Monthly Growth</option>
                            <option value="custom">Custom Campaign</option>
                            <option value="all">All Plans</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Max Uses</label>
                          <select
                            value={codeForm.max_uses}
                            onChange={(e) => setCodeForm({ ...codeForm, max_uses: Number(e.target.value) })}
                            className="perkly-input"
                          >
                            <option value={1}>1 use</option>
                            <option value={3}>3 uses</option>
                            <option value={5}>5 uses</option>
                            <option value={9999}>Unlimited</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Expiry Date</label>
                          <input
                            type="date"
                            value={codeForm.expires_at}
                            onChange={(e) => setCodeForm({ ...codeForm, expires_at: e.target.value })}
                            className="perkly-input"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-1.5">Notes (internal)</label>
                        <textarea
                          value={codeForm.notes}
                          onChange={(e) => setCodeForm({ ...codeForm, notes: e.target.value })}
                          rows={2}
                          placeholder="e.g. Given to The Grill House for purchasing monthly plan on 4/5/2026"
                          className="perkly-input resize-none"
                        />
                      </div>
                    </form>
                  </div>

                  <div className="px-7 py-5 border-t border-[#8A9A8B]/12 flex gap-3 flex-shrink-0 bg-white rounded-b-3xl">
                    <button type="button" onClick={() => setShowCodeForm(false)} className="flex-1 btn-secondary text-sm py-3 justify-center">Cancel</button>
                    <button form="plan-code-form" type="submit" disabled={codeFormLoading} className="flex-1 btn-primary text-sm py-3 justify-center disabled:opacity-60" data-testid="create-plan-code-submit">
                      {codeFormLoading ? 'Generating...' : 'Generate Code'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ANALYTICS */}
        {tab === 'analytics' && (
          <div data-testid="admin-analytics-panel">
            {loading || !analytics ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* Overview metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: 'Total Claims', value: analytics.overview.total_claims, color: '#C8A97E', sub: `+${analytics.overview.claims_today} today` },
                    { label: 'This Week', value: analytics.overview.claims_week, color: '#7BA05B', sub: 'student claims' },
                    { label: 'Conversion', value: `${analytics.overview.avg_conversion}%`, color: '#A07BC8', sub: 'views → claims' },
                    { label: 'Push Subscribers', value: analytics.overview.push_subscribers, color: '#C87B7B', sub: 'push opt-ins' },
                  ].map((m) => (
                    <div key={m.label} className="bg-white rounded-2xl p-5 border border-[#8A9A8B]/10">
                      <p className="text-xs text-[#2B2B2B]/50 mb-1">{m.label}</p>
                      <p className="text-2xl font-bold" style={{ color: m.color, fontFamily: 'Playfair Display, serif' }}>{m.value}</p>
                      <p className="text-xs text-[#2B2B2B]/40 mt-1">{m.sub}</p>
                    </div>
                  ))}
                </div>

                {/* Claims by day chart (simple bar) */}
                <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10 mb-6">
                  <h3 className="font-bold text-[#2B2B2B] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Claims Last 14 Days</h3>
                  <div className="flex items-end gap-1.5 h-24">
                    {analytics.claims_by_day.map((d) => {
                      const max = Math.max(...analytics.claims_by_day.map((x) => x.claims), 1);
                      const h = Math.max((d.claims / max) * 100, 4);
                      return (
                        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                          <span className="text-xs text-[#C8A97E] font-bold opacity-0 group-hover:opacity-100 transition-opacity">{d.claims}</span>
                          <div
                            className="w-full rounded-t-lg transition-all duration-300 group-hover:opacity-80"
                            style={{ height: `${h}%`, background: d.claims > 0 ? '#C8A97E' : '#8A9A8B20', minHeight: '4px' }}
                            title={`${d.date}: ${d.claims} claims`}
                          />
                          <span className="text-xs text-[#2B2B2B]/30 hidden sm:block">{d.date.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Top deals by claims */}
                  <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10">
                    <h3 className="font-bold text-[#2B2B2B] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Top Deals by Claims</h3>
                    <div className="space-y-3">
                      {analytics.deals_performance.slice(0, 6).map((d, i) => (
                        <div key={d.deal_id} className="flex items-center gap-3">
                          <span className="text-sm font-bold text-[#C8A97E]/60 w-5">#{i + 1}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-[#2B2B2B] truncate">{d.title}</p>
                            <p className="text-xs text-[#8A9A8B]">{d.business_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-[#C8A97E]">{d.claim_count}</p>
                            <p className="text-xs text-[#8A9A8B]">{d.conversion}%</p>
                          </div>
                        </div>
                      ))}
                      {analytics.deals_performance.length === 0 && <p className="text-xs text-[#2B2B2B]/40">No claims yet</p>}
                    </div>
                  </div>

                  {/* Claims by category */}
                  <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10">
                    <h3 className="font-bold text-[#2B2B2B] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Claims by Category</h3>
                    <div className="space-y-3">
                      {Object.entries(analytics.claims_by_category).map(([cat, count]) => {
                        const maxCat = Math.max(...Object.values(analytics.claims_by_category), 1);
                        const pct = (count / maxCat) * 100;
                        const colors = { 'Food & Drink': '#C8A97E', 'Nightlife': '#6B7CFF', 'Fitness & Wellness': '#5DBB63', 'Activities': '#C07CFF', 'Free Deals': '#FF7C7C', 'Convenience Stores': '#5B9EA0', 'Liquor Stores': '#8B4A62' };
                        const col = colors[cat] || '#C8A97E';
                        return (
                          <div key={cat}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="font-semibold text-[#2B2B2B]">{cat}</span>
                              <span className="text-[#8A9A8B]">{count} claims</span>
                            </div>
                            <div className="h-2 bg-[#8A9A8B]/10 rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: col }} />
                            </div>
                          </div>
                        );
                      })}
                      {Object.keys(analytics.claims_by_category).length === 0 && <p className="text-xs text-[#2B2B2B]/40">No data yet</p>}
                    </div>
                  </div>
                </div>

                {/* Business performance */}
                <div className="bg-white rounded-2xl p-6 border border-[#8A9A8B]/10">
                  <h3 className="font-bold text-[#2B2B2B] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Business Performance</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#8A9A8B]/12">
                          {['Business', 'Deals', 'Total Claims', 'Total Views', 'Conversion'].map((h) => (
                            <th key={h} className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] pb-3 pr-4">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#8A9A8B]/8">
                        {analytics.business_performance.map((b) => (
                          <tr key={b.business_id} className="hover:bg-[#F6F1E9]/40">
                            <td className="py-3 pr-4 font-semibold text-sm text-[#2B2B2B]">{b.name}</td>
                            <td className="py-3 pr-4 text-sm text-[#2B2B2B]/60">{b.deal_count}</td>
                            <td className="py-3 pr-4 text-sm font-bold text-[#C8A97E]">{b.total_claims}</td>
                            <td className="py-3 pr-4 text-sm text-[#2B2B2B]/60">{b.total_views}</td>
                            <td className="py-3 text-sm text-[#8A9A8B]">
                              {b.total_views > 0 ? `${((b.total_claims / b.total_views) * 100).toFixed(1)}%` : '—'}
                            </td>
                          </tr>
                        ))}
                        {analytics.business_performance.length === 0 && (
                          <tr><td colSpan={5} className="py-6 text-xs text-[#2B2B2B]/40 text-center">No claim data yet</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Revenue */}
                <div className="mt-6 bg-[#2B2B2B] rounded-2xl p-6 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-[#C8A97E] mb-1">Total Revenue</p>
                    <p className="text-3xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>${analytics.overview.revenue_usd.toFixed(2)}</p>
                    <p className="text-white/50 text-xs mt-1">From {transactions.filter(t => t.payment_status === 'paid').length} paid subscriptions</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-white/50 mb-1">Total Views</p>
                    <p className="text-2xl font-bold text-[#C8A97E]">{analytics.overview.total_views.toLocaleString()}</p>
                    <p className="text-white/50 text-xs mt-1">across all deals</p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* PAYMENTS */}
        {tab === 'payments' && (
          <div data-testid="admin-payments-panel">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Payment Transactions</h2>
                <p className="text-xs text-[#2B2B2B]/55 mt-0.5">All Stripe payments from businesses purchasing subscriptions.</p>
              </div>
              <div className="flex gap-3">
                <div className="bg-white rounded-xl border border-[#8A9A8B]/12 px-4 py-2 text-center">
                  <p className="text-xs text-[#2B2B2B]/50">Total Revenue</p>
                  <p className="text-xl font-bold text-[#C8A97E]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    ${transactions.filter(t => t.payment_status === 'paid').reduce((s, t) => s + (t.amount || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-white rounded-xl border border-[#8A9A8B]/12 px-4 py-2 text-center">
                  <p className="text-xs text-[#2B2B2B]/50">Paid</p>
                  <p className="text-xl font-bold text-green-600" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {transactions.filter(t => t.payment_status === 'paid').length}
                  </p>
                </div>
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : transactions.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-[#8A9A8B]/10">
                <p className="text-4xl mb-4">💳</p>
                <h3 className="font-bold text-[#2B2B2B] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>No Payments Yet</h3>
                <p className="text-sm text-[#2B2B2B]/55">Stripe payments will appear here when businesses purchase plans.</p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#8A9A8B]/10 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F6F1E9] border-b border-[#8A9A8B]/15">
                        {['Business', 'Plan', 'Amount', 'Status', 'Date', 'Session ID'].map((h) => (
                          <th key={h} className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-4">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#8A9A8B]/10">
                      {transactions.map((txn) => (
                        <tr key={txn.transaction_id} className="hover:bg-[#F6F1E9]/40" data-testid={`txn-row-${txn.transaction_id}`}>
                          <td className="px-4 py-3 font-semibold text-sm text-[#2B2B2B]">{txn.business_name}</td>
                          <td className="px-4 py-3">
                            <span className="text-xs bg-[#8A9A8B]/10 text-[#8A9A8B] px-2 py-1 rounded-full capitalize">{txn.plan_name || txn.plan_id}</span>
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-[#2B2B2B]">${txn.amount?.toFixed(2)} {txn.currency?.toUpperCase()}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                              txn.payment_status === 'paid' ? 'bg-green-100 text-green-600'
                              : txn.status === 'expired' ? 'bg-red-100 text-red-500'
                              : 'bg-yellow-100 text-yellow-600'
                            }`}>
                              {txn.payment_status === 'paid' ? '✓ Paid' : txn.status === 'expired' ? 'Expired' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-[#2B2B2B]/50">{txn.created_at?.slice(0, 10)}</td>
                          <td className="px-4 py-3 text-xs text-[#2B2B2B]/40 font-mono">{txn.session_id?.slice(0, 20)}...</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* REFERRALS */}
        {tab === 'referrals' && (
          <div data-testid="admin-referrals-panel">
            <div className="flex items-start justify-between mb-1 gap-4 flex-wrap">
              <div>
                <h2 className="text-xl font-bold text-[#2B2B2B]" style={{ fontFamily: 'Playfair Display, serif' }}>Growth Network</h2>
                <p className="text-xs text-[#2B2B2B]/55 mt-0.5">Verified members introduced by each business via QR referral.</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <button
                  onClick={handleRecalculateReferrals}
                  disabled={recalcLoading}
                  className="px-4 py-2 rounded-xl bg-[#2B2B2B] text-white text-xs font-bold hover:bg-[#1E1A14] disabled:opacity-50 transition-colors"
                  data-testid="recalculate-referrals-btn"
                >
                  {recalcLoading ? 'Running…' : 'Recalculate All Members'}
                </button>
                {recalcResult && (
                  <p className={`text-xs font-semibold ${recalcResult.success ? 'text-green-600' : 'text-red-500'}`}>
                    {recalcResult.message}
                  </p>
                )}
              </div>
            </div>

            {loading || !adminReferrals ? (
              <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-4 border-[#C8A97E] border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <>
                {/* Summary — removed QR Scans card (inaccurate), kept accurate metrics */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-6">
                  {[
                    { label: 'Businesses', value: adminReferrals.summary.total_businesses, color: '#C8A97E' },
                    { label: 'Verified Members', value: adminReferrals.summary.total_members, color: '#5DBB63' },
                    { label: 'Total Credits Earned', value: adminReferrals.summary.total_credits, color: '#A07BC8' },
                  ].map(s => (
                    <div key={s.label} className="bg-white rounded-2xl p-4 border border-[#8A9A8B]/10 text-center">
                      <p className="text-2xl font-bold" style={{ color: s.color, fontFamily: 'Playfair Display, serif' }}>{s.value}</p>
                      <p className="text-xs text-[#2B2B2B]/50">{s.label}</p>
                    </div>
                  ))}
                </div>

                {/* Business referral table — Scans column removed */}
                <div className="bg-white rounded-3xl border border-[#8A9A8B]/10 overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-[#F6F1E9] border-b border-[#8A9A8B]/15">
                          {['Rank', 'Business', 'Badge', 'Members Introduced', 'Credits Earned', 'Referral URL'].map(h => (
                            <th key={h} className="text-left text-xs font-bold uppercase tracking-wider text-[#8A9A8B] px-4 py-3">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#8A9A8B]/10">
                        {adminReferrals.businesses.map((b, i) => (
                          <tr key={b.business_id} className="hover:bg-[#F6F1E9]/40">
                            <td className="px-4 py-3 text-sm font-bold text-[#2B2B2B]">#{i + 1}</td>
                            <td className="px-4 py-3">
                              <p className="font-semibold text-sm text-[#2B2B2B]">{b.business_name}</p>
                              <p className="text-xs text-[#8A9A8B]">{b.business_email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-sm">{b.badge_emoji}</span>
                              <span className="text-xs text-[#2B2B2B]/60 ml-1">{b.badge}</span>
                            </td>
                            <td className="px-4 py-3 text-sm font-bold text-[#5DBB63]">{b.total_members}</td>
                            <td className="px-4 py-3 text-sm font-bold text-[#C8A97E]">{b.total_credits}</td>
                            <td className="px-4 py-3">
                              <a href={b.referral_url} target="_blank" rel="noreferrer" className="text-xs text-[#C8A97E] underline">{b.referral_slug}</a>
                            </td>
                          </tr>
                        ))}
                        {adminReferrals.businesses.length === 0 && (
                          <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-[#2B2B2B]/40">No referral data yet.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* SITE SETTINGS */}
        {tab === 'settings' && (
          <div data-testid="admin-settings-panel">
            <h2 className="text-xl font-bold text-[#2B2B2B] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>Site Settings</h2>
            <p className="text-xs text-[#2B2B2B]/55 mb-6">Admin-only controls for the public website appearance.</p>

            {/* Hero Image Section */}
            <div className="bg-white rounded-3xl border border-[#8A9A8B]/10 p-7 mb-6">
              <h3 className="font-bold text-[#2B2B2B] mb-1">Homepage Hero Background Image</h3>
              <p className="text-xs text-[#2B2B2B]/55 mb-5">Change the full-screen background image on the homepage. Changes go live immediately after saving.</p>

              {/* Preview */}
              {heroPreview && (
                <div className="relative rounded-2xl overflow-hidden mb-5" style={{ height: '200px' }}>
                  <img src={heroPreview} alt="Hero preview" className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  <div className="absolute inset-0" style={{ backgroundColor: `rgba(43,43,43,${(heroSettings.overlay_opacity || 55) / 100})` }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-white text-lg font-bold" style={{ fontFamily: 'Playfair Display, serif' }}>Preview</p>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {/* Upload from device */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-2">Upload Image from Device</label>
                  <input ref={heroFileRef} type="file" accept="image/*" onChange={handleHeroImageFile} className="hidden" data-testid="hero-file-input" />
                  <button
                    onClick={() => heroFileRef.current?.click()}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-dashed border-[#8A9A8B]/30 text-sm font-semibold text-[#2B2B2B] hover:border-[#C8A97E] transition-colors"
                  >
                    📁 Upload Photo (JPG, PNG, WebP — max 5MB)
                  </button>
                </div>

                {/* Or paste URL */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-2">Or Paste Image URL</label>
                  <input
                    type="url"
                    value={heroSettings.image_url}
                    onChange={e => { setHeroSettings(s => ({ ...s, image_url: e.target.value })); setHeroPreview(e.target.value); }}
                    placeholder="https://example.com/your-photo.jpg"
                    className="perkly-input"
                    data-testid="hero-url-input"
                  />
                </div>

                {/* Overlay opacity */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-[#8A9A8B] block mb-2">
                    Dark Overlay Opacity: {heroSettings.overlay_opacity}%
                  </label>
                  <input
                    type="range" min={20} max={85}
                    value={heroSettings.overlay_opacity}
                    onChange={e => setHeroSettings(s => ({ ...s, overlay_opacity: Number(e.target.value) }))}
                    className="w-full accent-[#C8A97E]"
                    data-testid="hero-opacity-slider"
                  />
                  <div className="flex justify-between text-xs text-[#8A9A8B] mt-1">
                    <span>20% (brighter)</span>
                    <span>85% (darker)</span>
                  </div>
                </div>

                {heroMsg && (
                  <p className={`text-sm font-semibold px-4 py-2.5 rounded-xl ${heroMsg.startsWith('✓') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                    {heroMsg}
                  </p>
                )}

                <button
                  onClick={saveHeroSettings}
                  disabled={heroLoading || !heroSettings.image_url}
                  className="btn-primary text-sm px-8 py-3 disabled:opacity-60"
                  data-testid="save-hero-btn"
                >
                  {heroLoading ? 'Saving...' : '💾 Save Hero Image'}
                </button>
              </div>
            </div>

            {/* Contact info display */}
            <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 p-6 mb-6">
              <h3 className="font-bold text-[#2B2B2B] mb-3">Notification Center</h3>
              <p className="text-xs text-[#2B2B2B]/55 mb-4">Send announcements to businesses and customers. All emails sent via Resend.</p>

              <div className="space-y-3">
                {/* Growth Network blast */}
                {[
                  {
                    id: 'growth',
                    icon: '🚀',
                    title: 'Announce Growth Network to All Businesses',
                    desc: 'Sends each business their referral QR link, badge system details, and marketing kit instructions. Creates missing referral profiles automatically.',
                    endpoint: '/admin/notifications/send-growth-email',
                    btnLabel: 'Send to All Businesses',
                    btnColor: '#C8A97E',
                  },
                  {
                    id: 'deals',
                    icon: '🔥',
                    title: "Send Today's Deals Email to All Users",
                    desc: 'Sends the top 5 active deals to all registered users and email subscribers.',
                    endpoint: '/admin/notifications/send-deals-email',
                    btnLabel: "Send Deals Digest",
                    btnColor: '#2B2B2B',
                  },
                  {
                    id: 'push',
                    icon: '🔔',
                    title: 'Send Push Notification Now',
                    desc: 'Send an instant push notification to all subscribers with push enabled.',
                    endpoint: '/admin/notifications/send',
                    btnLabel: 'Send Push Now',
                    btnColor: '#8A9A8B',
                    body: { title: '🔥 New Deals on Perkly!', message: 'Check out today\'s best local deals.', url: '/explore' },
                  },
                  {
                    id: 'backfill',
                    icon: '🔧',
                    title: 'Backfill Referral Profiles',
                    desc: 'Creates referral profiles (QR codes, slugs) for any approved businesses that are missing them.',
                    endpoint: '/admin/notifications/backfill-referrals',
                    btnLabel: 'Run Backfill',
                    btnColor: '#5DBB63',
                  },
                ].map(item => (
                  <NotificationAction key={item.id} {...item} API={API} showMsg={showMsg} />
                ))}
              </div>
            </div>

            {/* Contact info */}
            <div className="bg-white rounded-2xl border border-[#8A9A8B]/10 p-6">
              <h3 className="font-bold text-[#2B2B2B] mb-3">Contact Information (Live)</h3>
              <div className="space-y-2 text-sm text-[#2B2B2B]/70">
                <p>📧 Customer Support: <a href="mailto:support@useperkly.com" className="text-[#C8A97E] font-semibold">support@useperkly.com</a></p>
                <p>🤝 Business Enquiries: <a href="mailto:partners@useperkly.com" className="text-[#C8A97E] font-semibold">partners@useperkly.com</a></p>
                <p>📸 Instagram: <a href="https://www.instagram.com/perklydeals2026" target="_blank" rel="noreferrer" className="text-[#C8A97E] font-semibold">@perklydeals2026</a></p>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
